import { computed, watch, type ComputedRef, type Ref } from 'vue'
import { useRoute, type RouteLocationNormalizedLoaded } from 'vue-router'
import {
  canUseOrganigrammeEditMode,
} from '~~/utils/auth/abilities'
import {
  canActivateOrganigrammeEditOnRoute,
  isOrganigrammeContentRoutePath,
} from '~~/utils/auth/organigrammeEditRouteGate'
import { useOrganigrammeEditRouteAccess } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import { useEffectiveGardianRoles } from '~/composables/auth/useEffectiveGardianRoles'
import { useDeploymentMaintenance } from '~/composables/api/useDeploymentMaintenance'
import { useAppToolboxAvailability } from '~/composables/ui/useAppToolboxAvailability'
import { useOrgStore } from '~~/stores/org'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { displayKeysForShortcut } from '~/composables/ui/useUserShortcuts'
import { useUserPreferences } from '~/composables/ui/useUserPreferences'
import type { ManagerOrganigramEditScope, MeOrgPosition } from '~/composables/equipe/useMyOrgPositions'

type UseAppLayoutEditModeParams = {
  loggedIn: Ref<boolean>
  isEditModeBlockedByOnboarding: ComputedRef<boolean>
}

type EditModeStore = {
  isEditing?: boolean
  setIsEditing?: (value: boolean) => void
} | null

type EditModeToast = {
  info: (title: string, description: string) => void
}

type EditModeMaintenance = {
  state: { value: { active: boolean } }
  isWriteBlocked: { value: boolean }
  forceExitEditMode: { value: boolean }
}

type EditModeRuntime = {
  store: EditModeStore
  toast: EditModeToast
  route: RouteLocationNormalizedLoaded
  roles: { value: string[] }
  directionAdminDirLibs: { value: string[] }
  myOrgPositions: { value: MeOrgPosition[] }
  myManagerOrganigramEditScope: { value: ManagerOrganigramEditScope | null }
  maintenance: EditModeMaintenance
  loggedIn: Ref<boolean>
  isEditModeBlockedByOnboarding: Ref<boolean>
  isEditing: Ref<boolean>
  canEditOrganigramOnCurrentView: Ref<boolean>
}

const EDIT_MODE_UNAVAILABLE_TITLE = 'Mode édition indisponible'
const EDIT_MODE_DISABLED_TITLE = 'Mode édition désactivé'
const MAINTENANCE_TITLE = 'Maintenance en cours'

function resolveOrgStore(): EditModeStore {
  try {
    return useOrgStore()
  } catch {
    return null
  }
}

function readStoreEditing(store: EditModeStore): boolean {
  return store?.isEditing ?? false
}

function setOrganigrammeEditing(store: EditModeStore, value: boolean): void {
  store?.setIsEditing?.(value)
}

function refuseEditMode(runtime: EditModeRuntime, message: string): void {
  runtime.toast.info(EDIT_MODE_UNAVAILABLE_TITLE, message)
  setOrganigrammeEditing(runtime.store, false)
}

function routeRefusalMessage(path: string): string {
  if (!isOrganigrammeContentRoutePath(path)) {
    return 'Le mode édition n’est disponible que sur l’organigramme (accueil national, une direction ou une fiche équipe).'
  }
  return 'Sur cette page, votre périmètre ne permet pas d’activer le mode édition. Ouvrez la direction ou l’équipe concernée.'
}

function canActivateOnCurrentRoute(runtime: EditModeRuntime): boolean {
  return canActivateOrganigrammeEditOnRoute(
    runtime.route.path,
    runtime.route,
    runtime.myOrgPositions.value,
    runtime.roles.value,
    {
      directionAdminDirLibs: runtime.directionAdminDirLibs.value,
      managerOrganigramEditScope: runtime.myManagerOrganigramEditScope.value,
    },
  )
}

function blockEditModeActivation(runtime: EditModeRuntime): boolean {
  if (runtime.isEditModeBlockedByOnboarding.value) {
    refuseEditMode(
      runtime,
      'Le mode édition est désactivé pendant cette formation. Terminez le tutoriel pour l’activer.',
    )
    return true
  }
  if (!runtime.loggedIn.value) {
    setOrganigrammeEditing(runtime.store, false)
    return true
  }
  if (!canUseOrganigrammeEditMode(runtime.roles.value, runtime.directionAdminDirLibs.value)) {
    refuseEditMode(runtime, 'Vous n’avez pas les droits nécessaires pour activer le mode édition.')
    return true
  }
  if (!canActivateOnCurrentRoute(runtime)) {
    refuseEditMode(runtime, routeRefusalMessage(runtime.route.path))
    return true
  }
  if (runtime.maintenance.state.value.active) {
    refuseEditMode(
      runtime,
      'Le mode édition est temporairement indisponible pendant la maintenance, pour éviter que vous saisissiez des modifications qui pourraient ne pas être enregistrées.',
    )
    return true
  }
  return false
}

function applyEditModeRequest(runtime: EditModeRuntime, value: boolean): void {
  if (!value) {
    setOrganigrammeEditing(runtime.store, false)
    return
  }
  if (blockEditModeActivation(runtime)) {
    return
  }
  setOrganigrammeEditing(runtime.store, true)
}

function editModeDisabledDetail(loggedIn: boolean): string {
  if (!loggedIn) {
    return 'Vous avez été déconnecté.'
  }
  return 'Cette page ou votre périmètre ne permet plus le mode édition.'
}

function syncEditModeWithRouteAndScope(
  runtime: EditModeRuntime,
  current: readonly boolean[],
  previous: readonly (boolean | undefined)[] | undefined,
): void {
  const isEditingNow = current[0] === true
  const canEditNow = current[1] === true
  if (!isEditingNow || canEditNow) {
    return
  }
  setOrganigrammeEditing(runtime.store, false)
  // previous[1] vaut false seulement si, déjà monté, l’édition était déjà interdite.
  // undefined : premier passage, ou droit pas encore connu (changement de layout /admin).
  if (previous?.[1] === false) {
    return
  }
  runtime.toast.info(EDIT_MODE_DISABLED_TITLE, editModeDisabledDetail(runtime.loggedIn.value))
}

function onMaintenanceForceExit(runtime: EditModeRuntime, mustExit: boolean): void {
  if (!mustExit) {
    return
  }
  if (runtime.isEditing.value) {
    setOrganigrammeEditing(runtime.store, false)
    runtime.toast.info(
      EDIT_MODE_DISABLED_TITLE,
      'La dernière mise à jour a été enregistrée. Le mode édition est désactivé pendant la maintenance.',
    )
  }
  runtime.maintenance.forceExitEditMode.value = false
}

function onMaintenanceBecameActive(
  runtime: EditModeRuntime,
  activeNow: boolean,
  activeBefore: boolean | undefined,
): void {
  if (!activeNow || activeBefore) {
    return
  }
  if (!runtime.isEditing.value) {
    return
  }
  if (runtime.maintenance.isWriteBlocked.value) {
    setOrganigrammeEditing(runtime.store, false)
    runtime.toast.info(
      MAINTENANCE_TITLE,
      'Le mode édition est désactivé pendant la maintenance, pour éviter des modifications qui pourraient ne pas être enregistrées.',
    )
    return
  }
  runtime.toast.info(
    MAINTENANCE_TITLE,
    'Vous pouvez enregistrer la modification en cours une dernière fois. Ensuite, le mode édition sera désactivé pendant la maintenance.',
  )
}

function onEditModeBlockedByOnboarding(runtime: EditModeRuntime, blocked: boolean): void {
  if (!blocked || !runtime.isEditing.value) {
    return
  }
  setOrganigrammeEditing(runtime.store, false)
}

function onConnectionRouteEntered(runtime: EditModeRuntime, onConnectionFlow: boolean): void {
  if (!onConnectionFlow) {
    return
  }
  setOrganigrammeEditing(runtime.store, false)
}

function bindAppLayoutEditModeEffects(
  runtime: EditModeRuntime,
  isConnectionRoute: Ref<boolean>,
): void {
  watch(
    [runtime.isEditing, runtime.canEditOrganigramOnCurrentView],
    (current, previous) => {
      syncEditModeWithRouteAndScope(runtime, current, previous)
    },
    { immediate: true },
  )

  watch(
    () => runtime.maintenance.forceExitEditMode.value,
    (mustExit) => {
      onMaintenanceForceExit(runtime, mustExit)
    },
  )

  watch(
    () => runtime.maintenance.state.value.active,
    (activeNow, activeBefore) => {
      onMaintenanceBecameActive(runtime, activeNow, activeBefore)
    },
  )

  watch(
    () => runtime.isEditModeBlockedByOnboarding.value,
    (blocked) => {
      onEditModeBlockedByOnboarding(runtime, blocked)
    },
    { immediate: true },
  )

  watch(isConnectionRoute, (onConnectionFlow) => {
    onConnectionRouteEntered(runtime, onConnectionFlow)
  })
}

export function useAppLayoutEditMode(params: UseAppLayoutEditModeParams) {
  const route = useRoute()
  const store = resolveOrgStore()
  const toast = useSourceToast()
  const maintenance = useDeploymentMaintenance()
  const { isConnectionRoute } = useAppToolboxAvailability()
  const { roles } = useEffectiveGardianRoles()
  const { prefs: userPrefs } = useUserPreferences()
  const {
    canEditOrganigramOnCurrentView,
    isOrganigrammeEditing,
    myOrgPositions,
    myManagerOrganigramEditScope,
    directionAdminDirLibs,
  } = useOrganigrammeEditRouteAccess()

  const isEditing = computed(() => readStoreEditing(store))
  const runtime: EditModeRuntime = {
    store,
    toast,
    route,
    roles,
    directionAdminDirLibs,
    myOrgPositions,
    myManagerOrganigramEditScope,
    maintenance,
    loggedIn: params.loggedIn,
    isEditModeBlockedByOnboarding: params.isEditModeBlockedByOnboarding,
    isEditing,
    canEditOrganigramOnCurrentView,
  }

  function trySetEditMode(value: boolean): void {
    applyEditModeRequest(runtime, value)
  }

  const isChecked = computed({
    get: () => isEditing.value,
    set: (value: boolean) => trySetEditMode(value),
  })

  bindAppLayoutEditModeEffects(runtime, isConnectionRoute)

  const editModeShortcutDeactivateHint = computed(() => {
    const parts = displayKeysForShortcut(userPrefs.value.shortcuts.editModeToggle)
    return `Raccourci pour désactiver : ${parts.join(' + ')}`
  })

  function syncEditCheckedOnMount(): void {
    isChecked.value = isEditing.value
  }

  return {
    isEditing,
    isOrganigrammeEditing,
    isChecked,
    canEditOrganigramOnCurrentView,
    trySetEditMode,
    editModeShortcutDeactivateHint,
    syncEditCheckedOnMount,
  }
}
